import { AppField, ChecklistFieldValue, Member, Team } from "@smartsuite/types";
import { useMemo } from "react";
import * as yup from "yup";

import { resolveDefaultValue } from "../../../../helpers/formDefaults";
import { UseFormFieldResult, useFormField } from "../../../../hooks/useFormField";
import { FormConfigApplicationStructureItem, FormConfigItem } from "../../../../types/form";
import { $t } from "../../../../utils/intl";
import { validateWithYup } from "../../../validator";
import { validatorText } from "../../../validator.text";
import { isChecklistAssigneeEnabled, isChecklistDueDateEnabled } from "../checklistFormOptions";
import { normalizeChecklistDefaultValue } from "../normalizeDefault";
import { useChecklistAssignees } from "../useChecklistAssignees";

const EMPTY_CHECKLIST_VALUE: ChecklistFieldValue = {
  items: [],
  total_items: 0,
  completed_items: 0,
};

export interface UseChecklistFieldArgs {
  name: string;
  field: FormConfigApplicationStructureItem;
  formItem: FormConfigItem;
  isItemConditionSatisfied?: boolean;
}

export interface UseChecklistFieldResult {
  formField: UseFormFieldResult<ChecklistFieldValue | undefined>;
  members: Member[];
  teams: Team[];
  isTeamAssignmentEnabled: boolean;
  /**
   * Per-form "Checklist Options". Unlike `new_entries_allowed` / `modify_defaults_allowed`,
   * which are field settings react-ui already honours, these two exist only on the form item
   * and map to react-ui's `showDueDate` / `showAssignee`.
   */
  isDueDateEnabled: boolean;
  isAssigneeEnabled: boolean;
  value: ChecklistFieldValue;
  appField: AppField;
}

/**
 * Owns the form-state side of the checklist control: the validated Formik field, the
 * read-only assignee members, and the resolved display value. Kept separate from the
 * component so the markup stays thin and this logic can be unit-tested in isolation.
 */
export const useChecklistField = ({
  name,
  field,
  formItem,
  isItemConditionSatisfied,
}: UseChecklistFieldArgs): UseChecklistFieldResult => {
  const requiredText = $t(validatorText.validationRequired);
  const formField = useFormField<ChecklistFieldValue | undefined>({
    name,
    validate: validateWithYup(
      yup.mixed().when({
        is: () => formItem.required && !formItem.params.read_only && isItemConditionSatisfied,
        then: (s) =>
          s.test(
            "checklistNotEmpty",
            requiredText,
            (value: ChecklistFieldValue | undefined) => !!value && value.total_items > 0
          ),
      })
    ),
  });

  // Skip the fetch when the field is hidden by conditional logic — the control returns null in
  // that case, so the members would never be rendered.
  const { members, teams, isTeamAssignmentEnabled } = useChecklistAssignees({
    fieldSlug: field.slug,
    enabled: !!isItemConditionSatisfied,
  });

  const isDueDateEnabled = isChecklistDueDateEnabled(formItem);
  const isAssigneeEnabled = isChecklistAssigneeEnabled(formItem);

  // The other two Checklist Options also exist on the field, but on a form they are opt-in:
  // both start off and only an explicit true on the form item turns them on. The field's own
  // value is deliberately ignored — the builder's checkboxes default to off regardless of the
  // field, so honouring the field here would let the form behave against what the builder shows.
  // react-ui reads both straight off `field.params`, so they are resolved by handing it a field
  // whose params already carry the answer — no react-ui change needed for these two, unlike the
  // due-date and assignee gates.
  const appField = useMemo<AppField>(() => {
    const merged = { ...field.params } as Record<string, unknown>;

    merged.new_entries_allowed = formItem.params.new_entries_allowed === true;
    merged.modify_defaults_allowed = formItem.params.modify_defaults_allowed === true;

    // `modify_defaults_allowed` alone is not enough: react-ui tells a locked default apart from
    // a respondent's own item by looking the item's id up in `field.params.default_value`. A
    // form's defaults usually come from the form item, whose ids exist nowhere on the field, so
    // resolve and normalize the same default that `getFormDefaultValues` seeded — otherwise the
    // lookup never matches and nothing is locked.
    merged.default_value =
      normalizeChecklistDefaultValue(resolveDefaultValue(formItem, field)) ?? EMPTY_CHECKLIST_VALUE;

    return { ...field, params: merged } as unknown as AppField;
  }, [
    field,
    formItem.params.new_entries_allowed,
    formItem.params.modify_defaults_allowed,
    formItem.params.default_value,
  ]);
  // Defaults are seeded into Formik `initialValues` by `getFormDefaultValues`
  // (checklist is in `FORM_DEFAULT_SUPPORTED_FIELDS`), so the live form value already
  // carries any configured default. We only fall back to an empty value for display.
  const value = formField.value ?? EMPTY_CHECKLIST_VALUE;

  return {
    formField,
    members,
    teams,
    isTeamAssignmentEnabled,
    isDueDateEnabled,
    isAssigneeEnabled,
    value,
    appField,
  };
};
